// src/app/modules/Visitors/pages/Visitors.jsx
import React, { useEffect, useState } from "react";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import * as actions from "../../../Dashbord/_redux/dashboard/Actions";
import { VisitorsTable } from "../database";
import { FlagVisitorModal } from "../components/FlagVisitorModal";
import { AddThreatModal } from "../components/AddThreatModal";
import { BlacklistModal } from "../components/BlacklistModal";
import './visitors.scss';

import {
  Users, Shield, AlertTriangle, Search,
  Download, Filter, ShieldAlert, ShieldOff,
} from 'lucide-react';

export function Visitors() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentPage,   setCurrentPage]   = useState(1);
  const [perPage]                          = useState(10);
  const [searchTerm,    setSearchTerm]    = useState('');
  const [riskFilter,    setRiskFilter]    = useState('');
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [showThreatModal,    setShowThreatModal]    = useState(false);
  const [showBlacklistModal, setShowBlacklistModal] = useState(false);
  const [selectedVisitor,    setSelectedVisitor]    = useState(null);

  const { org, visitor_stats, loading } = useSelector(
    (state) => ({
      org:           state.auth.organization,
      visitor_stats: state.dashboard.visitorStats,
      loading:       state.dashboard.actionsLoading,
    }),
    shallowEqual
  );

  useEffect(() => { init_functions(); }, []);
  useEffect(() => { if (org?.organization_id) fetchVisitors(); }, [currentPage, searchTerm, riskFilter]);

  function init_functions() {
    if (org?.organization_id) { fetchVisitors(); fetchStats(); }
  }

  function fetchVisitors() {
    const params = { page: currentPage, limit: perPage, organization_id: org.organization_id };
    if (searchTerm) params.search     = searchTerm;
    if (riskFilter) params.risk_level = riskFilter;
    dispatch(actions.fetchAllData('visitors', org.organization_id, params));
  }

  function fetchStats() {
    dispatch(actions.fetchAllData('visitor_stats', org.organization_id));
  }

  function handleSearch()        { setCurrentPage(1); fetchVisitors(); }
  function handleViewDetails(id) { navigate(`/visitors/view/${id}`); }
  function handleFlag(id)        { setSelectedVisitor(id); setShowFlagModal(true); }
  function handleAddThreat(id)   { setSelectedVisitor(id); setShowThreatModal(true); }
  function handleBlacklist(id)   { setSelectedVisitor(id); setShowBlacklistModal(true); }

  const statCards = [
    { icon: <Users size={18} />,       bg: '#F0EEFF', color: '#5341C4', label: 'Total Visitors', value: visitor_stats?.total_visitors || 0 },
    { icon: <Shield size={18} />,      bg: '#F0FDF4', color: '#16a34a', label: 'Low Risk',        value: visitor_stats?.risk_distribution?.low || 0 },
    { icon: <AlertTriangle size={18}/>, bg: '#FFFBEB', color: '#D97706', label: 'High Risk',       value: visitor_stats?.risk_distribution?.high || 0 },
    { icon: <ShieldAlert size={18} />, bg: '#FEF2F2', color: '#DC2626', label: 'Critical Risk',   value: visitor_stats?.risk_distribution?.critical || 0 },
  ];

  return (
    <>
      <div className="visitors-page">

        {/* ── Header ── */}
        <div className="vt-header">
          <div className="vt-header__left">
            <h1>Visitor Profiles</h1>
            <p>{org?.name} · Manage and monitor all visitor profiles</p>
          </div>
          <button className="vt-btn vt-btn--secondary" onClick={() => {}}>
            <Download size={13} />Export
          </button>
        </div>

        {/* ── Stat cards ── */}
        <div className="vt-stats">
          {statCards.map((s, i) => (
            <div key={i} className="vt-stat">
              <div className="vt-stat__icon" style={{ background: s.bg, color: s.color }}>{s.icon}</div>
              <div>
                <div className="vt-stat__label">{s.label}</div>
                <div className="vt-stat__value">{s.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Filters ── */}
        <div className="vt-card">
          <div className="vt-card__body" style={{ padding: '11px 16px' }}>
            <div className="vt-filters">
              <div className="vt-search">
                <Search size={14} />
                <input
                  placeholder="Search by name, phone, or email…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
              </div>

              <select className="vt-select" value={riskFilter} onChange={(e) => { setRiskFilter(e.target.value); setCurrentPage(1); }}>
                <option value="">All Risk Levels</option>
                <option value="low">Low Risk</option>
                <option value="medium">Medium Risk</option>
                <option value="high">High Risk</option>
                <option value="critical">Critical Risk</option>
              </select>

              <button className="vt-apply-btn" onClick={handleSearch}>
                <Filter size={13} />Apply
              </button>
            </div>
          </div>
        </div>

        {/* ── Table ── */}
        <div className="vt-card">
          <div className="vt-card__header">
            <div className="vt-card__title"><Users size={14} />All Visitors</div>
            <span className="vt-card__meta">Complete visitor profile database</span>
          </div>
          <div className="vt-table-wrap">
            <VisitorsTable
              onViewDetails={handleViewDetails}
              onFlag={handleFlag}
              onAddThreat={handleAddThreat}
              onBlacklist={handleBlacklist}
              onPageChange={(p) => { setCurrentPage(p); fetchVisitors(); }}
              currentPage={currentPage}
              perPage={perPage}
            />
          </div>
        </div>

      </div>

      {/* ── Modals ── */}
      <FlagVisitorModal
        show={showFlagModal}
        onHide={() => { setShowFlagModal(false); setSelectedVisitor(null); }}
        profileId={selectedVisitor}
        onSuccess={fetchVisitors}
      />
      <AddThreatModal
        show={showThreatModal}
        onHide={() => { setShowThreatModal(false); setSelectedVisitor(null); }}
        profileId={selectedVisitor}
        organizationId={org?.organization_id}
        onSuccess={fetchVisitors}
      />
      <BlacklistModal
        show={showBlacklistModal}
        onHide={() => { setShowBlacklistModal(false); setSelectedVisitor(null); }}
        profileId={selectedVisitor}
        organizationId={org?.organization_id}
        onSuccess={fetchVisitors}
      />
    </>
  );
}